import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { getPreferences, savePreferences } from '@peruselab/api-client/preferences';
import type { User } from 'oidc-client-ts';
import { userManager } from '../auth/authClient';
import {
  createFavouriteStore,
  favouriteKey,
  parseFavouriteKey,
  MAX_FAVOURITE_TEMPLATES,
  type FavouriteRef
} from '../templates/favouriteStore';
export {
  favouriteKey,
  MAX_FAVOURITE_TEMPLATES,
  type FavouriteRef,
  type FavouriteKind
} from '../templates/favouriteStore';

const store = createFavouriteStore({
  storage: {
    getItem: (key) => globalThis.localStorage?.getItem(key) ?? null,
    setItem: (key, value) => globalThis.localStorage?.setItem(key, value)
  },
  load: getPreferences,
  save: savePreferences
});

/** The existing shelf, synchronized through the signed-in user's private preferences. */
export function useFavouriteTemplates() {
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  useEffect(() => {
    let active = true;
    let changed = false;
    const loaded = (user: User) => {
      changed = true;
      store.setAccount(user.expired ? null : user.profile.sub);
    };
    const unloaded = () => {
      changed = true;
      store.setAccount(null);
    };
    userManager.events.addUserLoaded(loaded);
    userManager.events.addUserUnloaded(unloaded);
    void userManager
      .getUser()
      .then((user) => {
        if (active && !changed) store.setAccount(user && !user.expired ? user.profile.sub : null);
      })
      .catch(() => {
        /* The browser shelf remains usable without authentication. */
      });
    const refresh = () => {
      void store.refresh();
    };
    window.addEventListener('online', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      active = false;
      userManager.events.removeUserLoaded(loaded);
      userManager.events.removeUserUnloaded(unloaded);
      window.removeEventListener('online', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);
  const keys = snapshot.keys;
  const favourites = useMemo(
    () => keys.map(parseFavouriteKey).filter((ref): ref is FavouriteRef => ref !== null),
    [keys]
  );
  const keySet = useMemo(() => new Set(keys), [keys]);
  const isFavourite = useCallback((ref: FavouriteRef) => keySet.has(favouriteKey(ref)), [keySet]);
  const canFavourite = useCallback(
    (ref: FavouriteRef) => keySet.has(favouriteKey(ref)) || keys.length < MAX_FAVOURITE_TEMPLATES,
    [keySet, keys.length]
  );
  return {
    favourites,
    count: keys.length,
    max: MAX_FAVOURITE_TEMPLATES,
    atCapacity: keys.length >= MAX_FAVOURITE_TEMPLATES,
    isFavourite,
    canFavourite,
    toggle: store.toggle,
    syncStatus: snapshot.status,
    retrySync: store.refresh
  };
}
export type FavouriteTemplates = ReturnType<typeof useFavouriteTemplates>;
